import React, { forwardRef, useRef, useState, useEffect } from 'react';
import { styled } from '@linaria/react';
import { useTheme, ITheme } from 'src/theme';
import { Validations } from '@utils/helpers';
import { isEmpty } from 'lodash';
import Button from '../Button'


const BoxFix = styled.div<InputProps>`
  font-size: 16px;
  font-family: ${props => props.theme.fontFamily};
  line-height: 20px;
  font-weight: bold;
  color: ${props => props.theme.colors.grey[500]};
  background-color:  ${props => props.theme.colors.grey[600]};
  margin: 0;
  padding: 13px 20px;
  position: absolute;
  top: ${props => (props.invalid) ? '1px' : '1px'};
  bottom: ${props => (props.invalid) ? '1px' : '1px'};
  left: ${props => (props.variant === 'prefix') ? '1px' : 'auto'};
  right: ${props => (props.variant === 'suffix') ? '1px' : 'auto'};
  border-radius: ${props => (props.variant === 'prefix') ? '4px 0 0 4px' : '0 4px 4px 0'};
  border-right: ${props => (props.variant === 'prefix') ? '1px' : '0px'} solid ${props => (props.invalid) ? props.theme.colors.red[200] : props.theme.colors.grey[100]};
  border-left: ${props => (props.variant === 'suffix') ? '1px' : '0px'} solid ${props => (props.invalid) ? props.theme.colors.red[200] : props.theme.colors.grey[100]};
  user-select: none; -webkit-user-select: none; -webkit-touch-callout: none;
`;

const Tag = styled.div<InputProps>`
  background-color: ${props => props.theme.colors.grey[600]};
  border-radius: 4px;
  border: 1px solid ${props => props.theme.colors.grey[600]};
  box-shadow: 0px 2px 4px 0px rgba(0,0,0,0.05); -webkit-box-shadow: 0px 2px 4px 0px rgba(0,0,0,0.05);
  color: ${props => props.theme.colors.primary};
  font-size: 14px;
  font-family: ${props => props.theme.fontFamily};
  line-height: 20px;
  margin: 0;
  padding: 4px 30px 4px 10px;
  position: absolute;
  top: 10px;
  left: 10px;
  user-select: none; -webkit-user-select: none; -webkit-touch-callout: none;
`;

const Cross = styled.div<InputProps>`
  cursor: pointer;
  display: inline-block;
  font-family: ${props => props.theme.fontFamily};
  font-size: 28px;
  transform: rotate(45deg);
  position: absolute;
  top: 4px;
  right: 9px;
  user-select: none; -webkit-user-select: none; -webkit-touch-callout: none;
`

const InputElement = styled.input<InputProps>`
  background-color: ${props => props.theme.colors.white};
  border: 1px solid ${props => (props.invalid) ? props.theme.colors.red[200] : props.theme.colors.grey[100]};
  border-radius: 4px;
  box-shadow: inset 0 2px 2px 0 rgba(204, 204, 204, 0.5);
  -webkit-box-shadow: inset 0 2px 2px 0 rgba(204, 204, 204, 0.5);
  box-sizing: border-box; 
  -webkit-box-sizing: border-box;
  color: ${props => props.readOnly ? props.theme.colors.grey[100] : props.theme.colors.grey[500]};
  flex-grow: ${props => (props.variant === 'button') ? '1' : '0'};
  font-family: ${props => props.theme.fontFamily};
  font-size: ${props => (props.variant === 'tag') ? '14px' : '16px'};
  letter-spacing: -0.1px;
  line-height: 24px;
  margin: ${props => (props.variant === 'button') ? '0 20px 0 0' : '0'};
  max-height: 50px;
  opacity: ${props => (props.disabled) ? '0.3' : '1'};
  outline: 0;
  padding: ${props => (props.variant === 'prefix') ? '13px 20px 13px 80px' : (typeof props.padding === 'number' ? props.padding + 'px' : props.padding + '')};
  transition: border-color .18s linear;
  width: 100%;
  min-width: ${props => props.minWidth  ? (props.minWidth + 'px') : 'auto'};
  -webkit-appearance: none;
  text-align: ${props => props.textAlign || 'unset'};
  &::placeholder {
    color: ${props => props.theme.colors.grey[100]};
    transition: color default;
  }
  &:focus {
    border: 1px solid ${props => (props.invalid) ? props.theme.colors.red[200] : props.theme.colors.blue[300]};
  }
  text-transform: ${props => props.textTransform || 'none'};
`;

const InputTagElement = styled.div<InputProps>`
  background-color: ${props => props.theme.colors.white};;
  border: 1px solid ${props => (props.invalid) ? props.theme.colors.red[200] : props.theme.colors.blue[300]};
  border-radius: 4px;
  box-shadow: inset 0 2px 2px 0 rgba(204, 204, 204, 0.5);
  -webkit-box-shadow: inset 0 2px 2px 0 rgba(204, 204, 204, 0.5);
  box-sizing: border-box; 
  -webkit-box-sizing: border-box;
  display: flex;
  margin: 0;
  outline: 0;
  padding: 13px 12px;
  transition: border-color .18s linear;
  min-width: ${props => (props.minWidth + 'px' || 'auto')};
  -webkit-appearance: none;
  &:focus {
    border: 1px solid ${props => (props.invalid) ? props.theme.colors.red[200] : props.theme.colors.blue[300]};
  }
  flex-wrap: wrap;
  min-height: 58px;

`

const Container = styled.div<StyledProps>`
  display: ${props =>
    (props.variant === 'button') ? 'flex' :
      (props.variant === 'inline') ? 'inline-block' : 'block'
  };
  padding: 0;
  border-radius: 4px;
  width: ${props => (props.variant === 'inline') ? 'auto' : '100%'};
  position: relative;
  margin: ${props => props.margin ? (typeof props.margin === 'number' ? `${props.margin}px` : props.margin) : 'unset'};
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  input[type=number] {
    -moz-appearance: textfield;
  }
  ${BoxFix} {
    border-color: ${props => props.inValid ? props.theme?.colors.red[200] : (props.isFocus ? props.theme?.colors.blue[300] : props.theme?.colors.grey[100])};      
  }
`;

interface StyledProps {
  variant?: string;
  isFocus?: boolean;
  inValid?: boolean;
  margin?: string | number;
  theme?: ITheme
}
export interface InputProps {
  variant?: 'default' | 'inline' | 'prefix' | 'suffix' | 'button' | 'tag';
  invalid?: boolean;
  inline?: boolean;
  theme?: any;
  buttonLabel?: string;
  boxLabel?: string;
  inputmode?: string;
  // Built in attr
  disabled?: boolean;
  placeholder?: string;
  value?: string | number | string[];
  margin?: number | string;
  padding?: number | string;
  type?: 'normal' | 'numeric' | 'number' | 'alphanumeric' | 'decimal';
  name?: string;
  id?: string;
  pattern?: string;
  max?: string;
  min?: string;
  minLength?: number;
  maxLength?: number;
  minWidth?: number;
  onChange?: (e: any) => void;
  onFocus?: (e: any) => void;
  onBlur?: (e: any) => void;
  onButtonClick?: (e: any) => void;
  onClearTag?: (e: string, index?: number) => void;
  ref?: any;
  textAlign?: 'center' | 'left' | 'right';
  readOnly?: boolean;
  textTransform?: string;
}

const Input = forwardRef<HTMLDivElement, InputProps>(
  ({
    variant = 'default',
    buttonLabel = 'Demo',
    boxLabel,
    placeholder,
    margin = '0 0 30px 0',
    value,
    type,
    onChange,
    onFocus,
    onBlur,
    invalid,
    onClearTag,
    onButtonClick,
    ...props
  }: InputProps, ref) => {
    const theme = useTheme();
    const inputRef = useRef<HTMLInputElement>(null);
    const [isFocus, setFocus] = useState<boolean>(false);
    const [inputValue, setInputValue] = useState<string | number | string[] | undefined>(value);

    const handleChange = (event: any): void => {
      const value = event.target.value;
      // console.log('Input ➤ handleChange', { value, inputValue });

      if (!isEmpty(value) && (type === 'decimal') && !Validations.isDecimal(value)) {
        // console.log('Input ➤ handleChange ➤ decimal check', { empty: !isEmpty(value), validation: !Validations.isDecimal(value) });
        setInputValue(inputValue);
        return;
      }
      if (!isEmpty(value) && (type === 'number' || type === 'numeric') && !Validations.isNumeric(value)) {
        // console.log('Input ➤ handleChange ➤ numeric check', { empty: !isEmpty(value), validation: !Validations.isNumeric(value) });
        setInputValue(inputValue);
        return;
      }
      if (!isEmpty(value) && type === 'alphanumeric' && !Validations.isAlphanumeric(value)) {
        // console.log('Input ➤ handleChange ➤ alphanumeric check', { empty: !isEmpty(value), validation: !Validations.isAlphanumeric(value) });
        setInputValue(inputValue);
        return;
      }
      setInputValue(value);
      if (onChange) {
        onChange(event);
      }
    }

    const handleClear = (value: string, index?: number) => (event: any): void => {
      if (typeof inputValue === 'string') {
        setInputValue('');
        if (inputRef.current) {
          inputRef.current.value = '';
        }
      } else {
        if (typeof index === 'number')
        if (Array.isArray(inputValue)) {
          const nextValue = inputValue.filter((item, i) => i!==index );
          setInputValue(nextValue);
        }
      }
    
      if (onClearTag) {
        onClearTag(value, index || 0);
      }
      
    }

    const handleBlur = (event: any): void => {
      setFocus(false);
      if (onBlur) {
        onBlur(event);
      }
    }

    const handleFocus = (event: any): void => {
      setFocus(true);
      if (onFocus) {
        onFocus(event);
      }
    }

    const handleButtonClick = (): void => {
      setFocus(false);
      if (onButtonClick) {
        onButtonClick(inputValue);
      }
    }

    useEffect(() => {
      // console.log('Input ➤ useEffect', { value, inputValue });
      if (value !== inputValue) {
        setInputValue(value);
      }
    }, [setInputValue, inputValue, value])

    return (
      <Container variant={variant} margin={margin} ref={ref} isFocus={isFocus} theme={theme} inValid={invalid}>
        {
          (variant === 'tag' && inputValue && Array.isArray(inputValue)) ? (
            <InputTagElement theme={theme} invalid={invalid}
            >
              {
                inputValue.map((text, index: number) => (
                  <Tag key={`${index}`} theme={theme} style={{ position: 'relative', top: 0, left: 0, marginRight: 10 }}>
                    {text}
                    <Cross theme={theme} onClick={handleClear(text, index)}>+</Cross>
                  </Tag>
                ))
              }
            </InputTagElement>
          ) : (
            <React.Fragment>
              {
                (variant === 'prefix' || variant === 'suffix') ? (
                  <BoxFix variant={variant} invalid={invalid} theme={theme} {...props}>{boxLabel}</BoxFix>
                ) : null
              }
              <InputElement
                ref={inputRef}
                value={`${inputValue}`}
                variant={variant}
                placeholder={placeholder}
                theme={theme}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={handleFocus}
                invalid={invalid}
                {...props}
              />
              {
                (variant === 'button') ? (
                  <Button onClick={handleButtonClick} variant='input' display='inline' label={buttonLabel}></Button>
                ) : null
              }
              {
                (variant === 'tag' && inputValue && typeof inputValue === 'string') ? (
                  <Tag theme={theme}>
                    {inputValue}
                    <Cross theme={theme} onClick={handleClear(inputValue)}>+</Cross>
                  </Tag>
                ) : null
              }
            </React.Fragment>
          )
        }


      </Container>
    );
  }
)

Input.defaultProps = {
  value: '',
  padding: '13px 20px',
  onChange: () => { }
}
export { Input };